/**
 * Checkout Store Public API
 * @see mobile: module-checkout/src/modules/checkout/stores/checkout-store.public-api.ts
 *
 * 100% matching mobile structure
 */

import { checkoutStoreActions } from './checkout-store.actions';
import type { CustomerAddress, Order, ShippingCartAddress } from '../../../types/front.graphql';
import type { Observable } from '@legendapp/state';
import { checkoutStore$, type CheckoutStore } from './checkout.store';
import { type AvailableShippingMethod } from '../models/shipping-method.model';
import { type AvailablePaymentMethod } from '../models/payment-method.model';

/**
 * PUBLIC API FOR CHECKOUT STORE STATE ACCESS
 *
 * This is the ONLY way external code should access checkout store.
 * Direct access to checkoutStore$ is FORBIDDEN.
 *
 * ARCHITECTURE PRINCIPLES:
 * - Encapsulation: Internal state hidden behind getters
 * - Single Responsibility: Each function has clear purpose
 * - Controlled Access: State mutations only through actions
 * - Type Safety: All functions properly typed
 */

// ============================================================================
// STATE GETTERS - READ-ONLY ACCESS
// ============================================================================

export const checkoutStoreStateAPI = {
    getSelectedShippingAddress: (): ShippingCartAddress | undefined =>
        checkoutStoreActions.getSelectedShippingAddress(),

    getSelectedBillingAddress: (): CustomerAddress | undefined => checkoutStoreActions.getSelectedBillingAddress(),

    getSelectedPaymentMethod: (): AvailablePaymentMethod | undefined => checkoutStoreActions.getSelectedPaymentMethod(),

    getCustomerNotes: (): string | undefined => checkoutStoreActions.getCustomerNotes(),

    getPlacedOrder: (): Order | undefined => checkoutStoreActions.getPlacedOrder(),

    getPlacedOrderId: (): string | undefined => checkoutStoreActions.getPlacedOrderId(),

    getLastPaymentMethodCode: (): string | undefined => checkoutStoreActions.getLastPaymentMethodCode(),
    // Observable access for reactive updates (advanced use cases)
    getPlacedOrderObservable: (): Observable<Order | undefined> => checkoutStore$.placedOrder,

    getSelectedShippingMethod: (): Observable<AvailableShippingMethod | undefined> =>
        checkoutStore$.selectedShippingMethod,
} as const;

// ============================================================================
// MUTATIONS - CONTROLLED ACCESS
// ============================================================================

export const checkoutStoreMutationAPI = {
    setSelectedShippingAddress: (address?: ShippingCartAddress): void =>
        checkoutStoreActions.setSelectedShippingAddress(address),

    setShippingMethod: (method?: AvailableShippingMethod): void =>
        checkoutStoreActions.setSelectedShippingMethod(method),

    setSelectedBillingAddress: (address?: CustomerAddress): void =>
        checkoutStoreActions.setSelectedBillingAddress(address),

    setSelectedPaymentMethod: (paymentMethod?: AvailablePaymentMethod): void =>
        checkoutStoreActions.setSelectedPaymentMethod(paymentMethod),

    setCustomerNotes: (notes?: string): void => checkoutStoreActions.setCustomerNotes(notes),

    setUpdatingAddress: (isUpdating: boolean): void => checkoutStoreActions.setUpdatingAddress(isUpdating),

    incrementDiscountVersion: (): void => checkoutStoreActions.incrementDiscountVersion(),

    /**
     * Store available shipping methods received from the SetShippingAddressesOnCart response.
     * Eliminates the redundant AvailableShippingMethods query after address change.
     */
    setAddressShippingMethodsData: (data: CheckoutStore['addressShippingMethodsData']): void =>
        checkoutStoreActions.setAddressShippingMethodsData(data),

    setPlacedOrder: (order?: Order): void => checkoutStoreActions.setPlacedOrder(order),

    setLastPaymentMethodCode: (code?: string): void => checkoutStoreActions.setLastPaymentMethodCode(code),
    /**
     * Consume placed order (get and clear in one operation)
     * USE THIS when displaying order confirmation to ensure it's only shown once
     *
     * @returns Order if exists, undefined otherwise
     * @example
     * const order = checkoutStoreAPI.actions.consumePlacedOrder();
     * if (order) {
     *   // Display order confirmation with order.order_number
     *   navigation.navigate('OrderSuccess', { orderId: order.order_number });
     * }
     */
    consumePlacedOrder: (): Order | undefined => checkoutStoreActions.consumePlacedOrder(),

    /**
     * Clear placed order without consuming it
     * USE THIS when you want to clear without reading the order
     */
    clearPlacedOrder: (): void => checkoutStoreActions.clearPlacedOrder(),

    /**
     * Clear checkout session data after order placement.
     * Clears shipping/billing address, payment method, shipping method,
     * customer notes, and updating flag.
     * PRESERVES placedOrder and lastPaymentMethodCode for the success/failed page.
     *
     * USE THIS right after placing an order to clean up session state
     * while keeping the one-time order display data intact.
     *
     * @example
     * checkoutStoreAPI.actions.setPlacedOrder(order);
     * checkoutStoreAPI.actions.setLastPaymentMethodCode(code);
     * checkoutStoreAPI.actions.clearCheckoutSessionData(); // clears everything else
     */
    clearCheckoutSessionData: (): void => checkoutStoreActions.clearCheckoutSessionData(),

    /**
     * Reset entire checkout store
     * USE THIS after successful order completion or when starting new checkout
     */
    resetCheckoutStore: (): void => checkoutStoreActions.resetCheckoutStore(),
} as const;

// ============================================================================
// UNIFIED PUBLIC API - SINGLE IMPORT POINT
// ============================================================================

/**
 * UNIFIED CHECKOUT STORE API
 *
 * This is the ONLY interface external code should use.
 *
 * USAGE EXAMPLES:
 * ```typescript
 * // READ OPERATIONS
 * const shippingAddress = checkoutStoreAPI.state.getSelectedShippingAddress();
 * const orderId = checkoutStoreAPI.state.getPlacedOrderId();
 *
 * // WRITE OPERATIONS
 * checkoutStoreAPI.actions.setSelectedPaymentMethod(paymentMethod);
 * checkoutStoreAPI.actions.setPlacedOrder(order);
 *
 * // CONSUME PATTERN (Get and Clear)
 * const order = checkoutStoreAPI.actions.consumePlacedOrder();
 * if (order) {
 *   console.log('Order placed:', order.order_number);
 * }
 *
 * // REACTIVE ACCESS (Advanced)
 * const placedOrder$ = checkoutStoreAPI.state.getPlacedOrderObservable();
 * ```
 */
export const checkoutStoreAPI = {
    state: checkoutStoreStateAPI,
    actions: checkoutStoreMutationAPI,
} as const;

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type CheckoutStoreStateAPI = typeof checkoutStoreStateAPI;
export type CheckoutStoreMutationAPI = typeof checkoutStoreMutationAPI;
export type CheckoutStoreAPI = typeof checkoutStoreAPI;
